import { useContext } from "react"
import { AgreementContext } from "../../../context/AgreementContext"

export const PrivacyPolicyText = () => {
  const { createAppLink } = useContext(AgreementContext)
  return (
    <>
      <p>Last Updated:&nbsp;March 15, 2023</p>
      <p>
        Teache exists to help build connections between people and make the
        world more knowledgeable and inclusive. In short, we aim to build a
        world where anyone can learn from others. We are a community built on
        trust. A fundamental part of earning that trust is being clear about how
        we use your information and protecting your human right to privacy.
      </p>
      <p>
        This Privacy Policy describes how Teache, Inc. and its affiliates
        (&ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo;
        or &ldquo;<strong>Teache</strong>&rdquo;), process personal information
        that we collect through the Teache Platform. The supplemental privacy
        pages listed below may apply to you depending on where you live and what
        you do on the Teache Platform. Please follow the links and review the
        supplemental information provided therein about how we process personal
        information for those regions and services.
      </p>
      <ol>
        <li>
          <strong> DEFINITIONS</strong>
        </li>
      </ol>
      <p>
        Undefined terms in this Privacy Policy have the same definition as in
        our&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>
        &nbsp;(&ldquo;<strong>Terms</strong>&rdquo;).
      </p>
      <ol start='2'>
        <li>
          <strong> PERSONAL INFORMATION WE COLLECT</strong>
        </li>
      </ol>
      <p>
        <strong>2.1 Information needed to use the Teache Platform</strong>.
      </p>
      <p>
        We collect personal information about you when you use the Teache
        Platform. Without it, we may not be able to provide you with all the
        services requested. This information includes:
      </p>
      <ul>
        <li>
          <strong>
            Contact Information, Accounts, and Profile Information
          </strong>
          , such as your first name, last name, phone number, postal address,
          email address, date of birth, and profile photo (some of which will
          depend on the features you use).
        </li>
        <li>
          <strong>Identity Verification and Payment Information</strong>, such
          as images of your government-issued ID (as permitted by applicable
          laws), your ID number or other&nbsp;verification&nbsp;information, and
          bank account or payment account information.
        </li>
      </ul>
      <p>
        <strong>2.2 Information you choose to give us</strong>.
      </p>
      <p>
        You can choose to provide us with additional personal information. This
        information may include:
      </p>
      <ul>
        <li>
          <strong>Additional Profile Information</strong>, such as gender,
          preferred language(s), city, and a personal description. As indicated
          in your account settings, some of this information is part of your
          public profile page and will be publicly visible.
        </li>
        <li>
          <strong>Address Book Contact Information</strong>. Address book
          contacts you import or enter manually.
        </li>
        <li>
          <strong>Other Information</strong>, such as when you fill in a form,
          add information to your account, respond to surveys, post to community
          forums, participate in promotions, communicate with our customer care
          team and other Members, or share your experience with us. This may
          include health information, if you choose to share it with us.
        </li>
      </ul>
      <p>
        <strong>
          2.3 Information is Automatically Collected when Using the Teache
          Platform and our Payment Services
        </strong>
        .
      </p>
      <p>
        When you use the Teache Platform and Payment Services, we automatically
        collect personal information. This information may include:
      </p>
      <ul>
        <li>
          <strong>Geo-location Information</strong>, such as precise or
          approximate location determined from your IP address or mobile
          device&rsquo;s GPS, depending on your device settings. We may also
          collect this information when you are not using the app, if you enable
          this through your settings or device permissions.
        </li>
        <li>
          <strong>Usage Information</strong>, such as the pages or content you
          view, searches for Listings or Bookings you have made, and other
          actions on the Teache Platform.
        </li>
        <li>
          <strong>Log Data and Device Information</strong>, such as details
          about how you have used the Teache Platform (including if you clicked
          on links to third-party applications), IP address, access dates and
          times, hardware and software information, device information, device
          event information, unique identifiers, crash data, cookie data, and
          the pages you have viewed or engaged with before or after using the
          Teache Platform. We may collect this information, even if you have not
          created a Teache account or logged in.
        </li>
        <li>
          <strong>
            Cookies and Similar Technologies as described in our&nbsp;
          </strong>
          <strong>
            <a href={createAppLink("/policies/cookie-policy")}>Cookie Policy</a>
          </strong>
          .
        </li>
        <li>
          <strong>Payment Transaction Information </strong>includes payment
          instrument used, date and time, payment amount, payment instrument
          expiration date and billing postcode, PayPal email address, IBAN
          information, your address, and other related transaction details.
        </li>
      </ul>
      <p>
        <strong>2.4 Personal Information We Collect from Third Parties</strong>.
      </p>
      <p>We collect personal information from other sources, such as:</p>
      <ul>
        <li>
          <strong>Third-Party Services</strong>. If you link, connect, or log in
          to the Teache Platform with a third-party service (e.g., Google,
          Facebook, WeChat), you direct the service to send us information, such
          as your registration, friends list, and profile information as
          controlled by that service or as authorized by you via your privacy
          settings at that service.
        </li>
        <li>
          <strong>Background Information</strong>. For Members in the United
          States, to the extent permitted by applicable laws, we may obtain
          reports from public records of criminal convictions or sex offender
          registrations. For Members outside of the United States, to the extent
          permitted by applicable laws and with your consent where required, we
          may obtain the local version of police, background, or registered sex
          offender checks. We may use your information, including your full name
          and date of birth, to obtain such reports.
        </li>
        <li>
          <strong>Other Sources</strong>. To the extent permitted by applicable
          law, we may receive additional information about you, such
          as&nbsp;references, demographic data, or information to help detect
          fraud and safety issues from third-party service providers and/or
          partners and combine it with information we have about you. For
          example, we may receive background check results or fraud warnings
          from identity verification service providers for use in our fraud
          prevention and risk assessment efforts. We may receive information
          about you and your activities on and off the Teache Platform; we may
          also receive information about your experiences and interactions from
          our partners. We may receive health information, including, but not
          limited to, health information related to contagious diseases.
        </li>
      </ul>
      <ol start='3'>
        <li>
          <strong> HOW WE USE THE INFORMATION WE COLLECT</strong>.
        </li>
      </ol>
      <p>
        <strong>3.1 Provide, Improve, and Develop the Teache Platform</strong>.
        We use personal information to:
      </p>
      <ul>
        <li>
          enable you to access the Teache Platform and make and receive
          payments.
        </li>
        <li>enable you to communicate with other Members.</li>
        <li>perform analytics, de-bug and conduct research.</li>
        <li>provide customer service.</li>
        <li>
          send you messages, updates, security alerts, and account
          notifications.
        </li>
        <li>
          personalize and customize your experience based on your interactions
          with the Teache Platform, your search and class booking history, your
          profile information and preferences, and other content you submit, and
        </li>
        <li>enable your use of our products.</li>
      </ul>
      <p>
        <strong>3.2 Create and Maintain a Trusted and Safer Environment</strong>
        . We use personal information to:
      </p>
      <ul>
        <li>
          detect and prevent fraud, spam, abuse, security and safety incidents,
          and other harmful activity.
        </li>
        <li>study and combat discrimination.</li>
        <li>conduct security investigations and risk assessments.</li>
        <li>verify or authenticate information provided by you.</li>
        <li>
          conduct checks against databases and other information sources,
          including background or police checks.
        </li>
        <li>
          comply with our legal obligations, protect the health and well-being
          of our Users, Instructors, Instructors&rsquo; employees, and members
          of the public.
        </li>
        <li>resolve disputes with our Members.</li>
        <li>enforce our agreements with third parties.</li>
        <li>
          comply with laws, respond to legal requests, prevent harm, and protect
          our rights (see Section 4.5).
        </li>
        <li>
          enforce our&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>{" "}
          and other policies, and
        </li>
        <li>
          in connection with the activities above, we may conduct profiling
          based on your interactions with the Teache Platform, your profile
          information and other content you submit to Teache, and information
          obtained from third parties. In limited cases, automated processes
          could restrict or suspend access to the Teache Platform if such
          processes detect activity that we think poses a safety or other risk
          to Teache, our community, or third parties. If you would like to
          challenge the decision based on the automated process, please get in
          touch with us via the Contact Information section below.
        </li>
      </ul>
      <p>
        <strong>
          3.3 Provide, Personalize, Measure, and Improve our Advertising and
          Marketing
        </strong>
        . We may use personal information to:
      </p>
      <ul>
        <li>
          send you promotional messages, marketing, advertising, and other
          information based on your preferences and social media advertising
          through social media platforms.
        </li>
        <li>personalize, measure, and improve our advertising.</li>
        <li>
          administer referral programs, rewards, surveys, sweepstakes, contests,
          or other promotional activities or events sponsored or managed by
          Teache or its third-party partners.
        </li>
        <li>
          analyze characteristics and preferences to send you promotional
          messages, marketing, advertising, and other information that we think
          might be of interest to you, and
        </li>
        <li>invite you to events and relevant opportunities.</li>
      </ul>
      <p>
        <strong>3.4 Provide Payment services</strong>. Personal information is
        used to enable or authorize third parties to use Payment Services to:
      </p>
      <ul>
        <li>
          detect and prevent money laundering, fraud, abuse, security incidents.
        </li>
        <li>conduct security investigations and risk assessments.</li>
        <li>
          comply with legal obligations (such as anti-money laundering
          regulations).
        </li>
        <li>
          enforce the&nbsp;
          <a href={createAppLink("/policies/payment-terms")}>Payment Terms</a>
          &nbsp;and other payment policies.
        </li>
        <li>
          with your consent, send you promotional messages, marketing,
          advertising, and other information that may be of interest to you
          based on your preferences, and
        </li>
        <li>provide and improve the Payment Services.</li>
      </ul>
      <ol start='4'>
        <li>
          <strong> SHARING &amp; DISCLOSURE</strong>
        </li>
      </ol>
      <p>
        <strong>4.1 Sharing With Your Consent or at Your Direction</strong>.
      </p>
      <p>
        Where you provide consent, we share your information as described at the
        time of consent, such as when authorizing a third-party application or
        website to access your Teache account or participating in promotional
        activities by Teache partners or third parties.
      </p>
      <p>
        Where permissible with applicable laws, we may use certain information
        about you, such as your email address, de-identify it, and share it with
        social media platforms to generate leads, drive traffic to Teach, or
        otherwise promote our products and services.
      </p>
      <p>
        <strong>4.2 Sharing Between Members.</strong>
      </p>
      <p>
        To help facilitate bookings or other interactions between Members, we
        may need to share certain information, such as:
      </p>
      <ul>
        <li>
          When a booking request is made, or a dispute is submitted, certain
          information may be shared between User(s) and Instructor(s), including
          profile, name, names of any additional Users, cancellation history,
          review information, age of User (unless prohibited by applicable law),
          dispute outcome (when applicable) and other information you choose to
          share and submit. When a booking is confirmed, additional information,
          such as a profile photo and a phone number, is shared to assist with
          coordinating the booking. When you as an Instructor have a confirmed
          booking, certain information is shared with the User (and the
          additional Users they invite, if applicable) to coordinate the
          booking, such as your profile, full name, phone number, and Listing
          address.
        </li>
        <li>
          When you, as an Instructor, invite another Member to manage your
          listing, you authorize that person to access and update your
          information and Member Content, including but not limited to certain
          information like your full name, phone number, address, calendar,
          Listing information, Listing photos, and email address.
        </li>
        <li>
          When you as a User invite additional Users to a booking, your full
          name, class dates, Instructor name, Listing details, class address,
          and other related information will be shared with each additional
          User.
        </li>
      </ul>
      <p>
        <strong>
          4.3 Information You Publish in Profiles, Listings, and Other Public
          Information
        </strong>
        .
      </p>
      <p>
        You can make certain information publicly visible to others, such as:
      </p>
      <ul>
        <li>
          Your public profile page includes your profile photo, first name,
          description, and city
        </li>
        <li>
          Listing pages that include information, such as a Class&rsquo;s
          approximate or precise location description, calendar availability,
          profile photo, aggregated demand information (such as page views over
          a period of time), and additional information you choose to share
        </li>
        <li>Reviews, ratings, and other public feedback</li>
        <li>
          Content in a community, discussion forum, blog, or social media post.
        </li>
      </ul>
      <p>
        We may display parts of your public profile and other Content you make
        available to the public, such as Listing details on third-party sites,
        platforms, and apps.
      </p>
      <p>
        Information you share publicly on the Teache Platform may be indexed
        through third-party search engines. In some cases, you may opt out of
        this feature in your account settings.
      </p>
      <p>
        <strong>4.4 Instructor Service Providers</strong>.
      </p>
      <p>
        Instructors may use third-party services (e.g., cleaning services) to
        help manage or deliver their own services. Instructors may use features
        on the Teache Platform to share information about the User (such as
        class start and end times, User names, and User phone numbers) with such
        third-party service providers.
      </p>
      <p>
        <strong>
          4.5 Complying with Laws, Responding to Legal Requests, Preventing
          Harm, and Protecting our Rights
        </strong>
        .
      </p>
      <p>
        We may disclose your information to courts, law enforcement,
        governmental or public authorities, tax authorities, or authorized third
        parties, if and to the extent we are required or permitted to do so by
        law or where disclosure is reasonably necessary (i) to comply with our
        legal obligations, (ii) to comply with a valid legal request or to
        respond to claims asserted against Teache, (iii) to respond to a valid
        legal request relating to a criminal investigation to address alleged or
        suspected illegal activity, or to respond to or address any other
        activity that may expose us, you, or any other of our users to legal or
        regulatory liability, (iv) to enforce and administer
        our&nbsp;agreements&nbsp;with Members, or (v) to protect the rights,
        property or personal safety of Teache, its employees, its Members, or
        members of the public. For example, if permitted due to the foregoing
        circumstances, Instructor tax information may be shared with tax
        authorities or other governmental agencies.
      </p>
      <p>
        Where appropriate, we may notify Members about legal requests, unless
        (i) providing notice is prohibited by the legal process itself, by court
        order we receive, or by applicable law, or (ii) we believe that
        providing notice would be futile, ineffective, create a risk of injury
        or bodily harm to an individual or group, create or increase a risk of
        fraud upon or harm to Teache, our Members, or expose Teache to a claim
        of obstruction of justice.
      </p>
      <p>
        For jurisdictions where Teache facilitates the collection and remittance
        of Taxes where legally permissible according to applicable law, we may
        disclose Instructors&rsquo; and Users&rsquo; information about
        transactions, class bookings Taxes to the applicable tax authority, such
        as Instructors&rsquo; and Users&rsquo; names, Users&rsquo; addresses,
        transaction dates and amounts, tax identification number(s), the amount
        of taxes received (or due) by Instructors from Users, and contact
        information.
      </p>
      <p>
        In jurisdictions where Teache facilitates or requires a registration,
        notification, permit, or license application of an Instructor with a
        local governmental authority through Teache in accordance with local
        law, we may share information about participating Instructors, such as
        the Instructor&rsquo;s full name and contact details, Class address, tax
        identification number, Listing details, and the number of classes booked
        with the relevant authority, both during the application process and
        periodically thereafter.
      </p>
      <p>
        <strong>4.6 Service Providers</strong>.
      </p>
      <p>
        We share personal information with affiliated and unaffiliated service
        providers to help us run our business, including service providers that
        help us (i) verify your identity or authenticate your identification
        documents, (ii) check information against public databases, (iii)
        conduct background or police checks, fraud prevention, and risk
        assessment, (iv) perform product development, maintenance and debugging,
        (v) allow the provision of the Teache Services through third-party
        platforms and software tools (e.g., through the integration with our
        APIs), (vi) provide customer service, advertising, or payments services,
        (vii) process, handle, or assess insurance claims or similar claims, or
        (viii) facilitate non-profit and charitable activities consistent with
        Teache's mission. These providers are contractually bound to protect
        your personal information and have access to your personal information
        to perform these tasks.
      </p>
      <p>
        <strong>4.7 Business Transfers</strong>.
      </p>
      <p>
        Suppose that Teache undertakes or is involved in any merger,
        acquisition, reorganization, sale of assets, bankruptcy, or insolvency
        event. In that case, we may sell, transfer, or share some or all of our
        assets, including your information in connection with such transaction
        or in contemplation of such transaction (e.g., due diligence). In this
        event, we will notify you before your personal information is
        transferred and becomes subject to a different privacy policy.
      </p>
      <p>
        <strong>4.8 Corporate Affiliates</strong>.
      </p>
      <p>
        To support us in providing, integrating, promoting, and improving the
        Teache Platform, Payment Services, and our affiliates&rsquo; services,
        we may share personal information within our corporate family of
        companies that are related by common ownership or control. One notable
        example is:
      </p>
      <ul>
        <li>
          <strong>Sharing with Teache, Inc</strong>. Even if your country of
          residence is not the United States, your information will be shared
          with Teache, Inc., which provides the technical infrastructure for the
          Teache Platform.
        </li>
      </ul>
      <ol start='5'>
        <li>
          <strong> OTHER IMPORTANT INFORMATION</strong>
        </li>
      </ol>
      <p>
        <strong>5.1 Analyzing your Communications</strong>.
      </p>
      <p>
        We may review, scan, or analyze your communications on the Teache
        Platform for reasons outlined in the &ldquo;How We Use Information We
        Collect&rdquo; section of this policy, including fraud prevention, risk
        assessment, regulatory compliance, investigation, product development,
        research, analytics, enforcing our&nbsp;
        <a href={createAppLink("/policies/terms")}>Terms of Service</a>, and
        customer support purposes. For example, as part of our fraud prevention
        efforts, we scan and analyze messages to mask contact information and
        references to other sites. In some cases, we may also scan, review, or
        analyze messages to debug, improve, and expand product offerings. We use
        automated methods, when reasonably possible. We may occasionally need to
        manually review communications, such as for fraud investigations and
        customer support, or to assess and improve the functionality of these
        automated tools. We will not review, scan, or analyze your messaging
        communications to send third-party marketing messages to you and we will
        not sell reviews or analyses of these communications.
      </p>
      <p>
        <strong>5.2 Linking Third-Party Accounts</strong>.
      </p>
      <p>
        You can link your Teache account with certain third-party services, such
        as social networks. Your contacts on these third-party services are
        referred to as &ldquo;Friends.&rdquo; When you direct the data sharing
        by creating this link:
      </p>
      <ul>
        <li>
          some of the information provided to us from linking accounts may be
          published on your public profile.
        </li>
        <li>
          your activities on the Teache Platform may be displayed to your
          Friends on the Teache Platform and/or that third-party service.
        </li>
        <li>
          a link to your public profile on that third-party service may be
          included in your Teache public profile.
        </li>
        <li>
          other Teache users may be able to see any Friends that you may have in
          common with them, or that you are a Friend of their Friend if
          applicable.
        </li>
        <li>
          other Teache users may be able to see any schools, hometowns, or other
          groups you have in common with them, as listed on your linked social
          networking service.
        </li>
        <li>
          information you provide to us from the linking of your accounts may be
          stored, processed, and transmitted for fraud prevention and risk
          assessment purposes, and
        </li>
        <li>
          publication and display of information that you provide to the Teache
          Platform through this linkage are subject to your settings and
          authorizations on the Teache Platform and the third-party service.
        </li>
      </ul>
      <p>
        <strong>5.3 Third-Party Partners &amp; Integrations</strong>.
      </p>
      <p>
        Parts of Teache may link to third-party services not owned or controlled
        by Teache, such as Google Maps/Earth. Use of these services is subject
        to the privacy policies of those providers. Teache does not own or
        control these third parties; when you interact with them, you are
        providing your information to them.
      </p>
      <ol start='6'>
        <li>
          <strong> YOUR RIGHTS</strong>
        </li>
      </ol>
      <p>
        You can exercise any of the rights described in this section consistent
        with applicable laws and submit requests for data subject rights. Please
        note that we may ask you to verify your identity and request before
        taking further action on your request.
      </p>
      <p>
        <strong>6.1 Managing Your Information</strong>.
      </p>
      <p>
        You can access and update some of your personal information through your
        Account settings. If you connected your Teache Account to a third-party
        service, such as Facebook or Google, you could change your settings and
        unlink from that service in your Account settings. You are responsible
        for keeping your personal information up to date.
      </p>
      <p>
        <strong>6.2 Data Access and Portability</strong>.
      </p>
      <p>
        In some jurisdictions, applicable laws may entitle you to request
        certain copies of your personal information or information about how we
        handle your personal information, request copies of personal information
        that you have provided to us in a structured, commonly used, and
        machine-readable format, and/or request that we transmit this
        information to another service provider (where technically feasible).
      </p>
      <p>
        <strong>6.3 Data Erasure</strong>.
      </p>
      <p>
        In certain jurisdictions, you can request that your personal information
        be deleted. Please note that if you request the erasure of your personal
        information:
      </p>
      <ul>
        <li>
          We may retain your personal information as necessary for our
          legitimate business interests, such as preventing money laundering,
          fraud detection and prevention, and enhancing safety. For example,
          suppose that we suspend a Teache Account for fraud or safety reasons.
          In that case, we may retain information from that Teache Account to
          prevent that Member from opening a new Teache Account in the future.
        </li>
        <li>
          We may retain and use your personal information to the extent
          necessary to comply with our legal obligations. For example, Teache
          and Teache Payments may keep information for tax, legal reporting and
          auditing obligations.
        </li>
        <li>
          Information you have shared with others (e.g., Reviews, forum
          postings) will continue to be publicly visible on Teache, even after
          your Teache Account is canceled. However, the attribution of such
          information to you will be removed. Some copies of your information
          (e.g., log records) will remain in our database. However, they are
          disassociated from personal identifiers.
        </li>
        <li>
          Because we take measures to protect data from accidental or malicious
          loss and destruction, residual copies of your personal information may
          not be removed from our backup systems for a limited period of time.
        </li>
      </ul>
      <ol start='7'>
        <li>
          <strong> SECURITY</strong>
        </li>
      </ol>
      <p>
        While no organization can guarantee perfect security, we continuously
        implement and update administrative, technical, and physical security
        measures to help protect your information against unauthorized access,
        loss, destruction, or alteration.
      </p>
      <ol start='8'>
        <li>
          <strong> CHANGES TO THIS PRIVACY POLICY</strong>
        </li>
      </ol>
      <p>
        We reserve the right to modify this Privacy Policy at any time in
        accordance with applicable law. If we do so, we will post the revised
        Privacy Policy and update the &ldquo;Last Updated&rdquo; date at the
        top. In case of material changes, we will also notify you of the
        modification by email at least thirty (30) days before the effective
        date. If you disagree with the revised Privacy Policy, you can cancel
        your Account. If you do not cancel your Account before the date the
        revised Privacy Policy becomes effective, your continued access to or
        use of the Teache Platform will be subject to the revised Privacy
        Policy.
      </p>
      <ol start='9'>
        <li>
          <strong> CONTACT INFORMATION AND RESPONSIBLE TEACHE ENTITIES</strong>
        </li>
      </ol>
      <p>
        For questions or complaints about this Privacy Policy or Teache's
        handling of personal information, contact Teache, Inc., Legal Privacy,
        79 Madison Ave, New York, NY 10016 USA; for payment-related matters,
        please use the contact information provided in the&nbsp;
        <a href={createAppLink("/policies/payment-terms")}>
          Payments Terms of Service
        </a>
        &nbsp;page.
      </p>
      <p>
        <strong>California </strong>
      </p>
      <p>
        If you reside in California, this section applies to you and supplements
        this Privacy Policy.
      </p>
      <ol>
        <li>
          <strong> CALIFORNIA RESIDENTS</strong>
        </li>
      </ol>
      <p>
        Teache will not share information it collects about you with its
        affiliates or third parties (both financial and non-financial) except as
        required or permitted by your state&rsquo;s law.
      </p>
      <ol start='2'>
        <li>
          <strong> CALIFORNIA ONLINE PRIVACY PROTECTION ACT (CALOPPA)</strong>
        </li>
      </ol>
      <p>
        We do not support Do Not Track (&ldquo;<strong>DNT&rdquo;</strong>). Do
        Not Track is a preference you can set in your web browser to inform
        websites that you do not want to be tracked.&nbsp; You can enable or
        disable Do Not Track by visiting your web browser's Preferences or
        Settings page.&nbsp; In addition to the rights, as explained in this
        Privacy Policy, California residents who provide Personal Information
        (as defined in the statute) to obtain products or services for personal,
        family, or household use are entitled to request and obtain from us once
        a calendar year, information about the Personal Information we shared,
        if any, with other businesses for marketing uses. If applicable, this
        information would include the categories of Personal Information and the
        names and addresses of those businesses with which we shared such
        personal information for the immediately prior calendar year (e.g.,
        requests made in the current year will receive information about the
        prior year). To obtain this information, please get in touch with us.
      </p>
      <p>
        We do not sell personal information to third parties. We do allow third
        parties to collect personal information through the&nbsp;Teache Platform
        and share personal information with third parties for the business
        purposes described in the Privacy Policy, including, without limitation,
        advertising and marketing on the Teache Platform&nbsp;and elsewhere
        based on users&rsquo; online activities over time and across Teache
        services.
      </p>
    </>
  )
}

export default PrivacyPolicyText
